import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, RegisterData } from '../types';
import { authAPI, impersonationAPI } from '../services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  loginWithBiometric: (token: string, userData: any) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'CLEAR_USER' }
  | { type: 'UPDATE_USER'; payload: User };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    
    case 'CLEAR_USER':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper para usar localStorage de forma segura
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('No se pudo guardar en localStorage:', e);
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('No se pudo eliminar de localStorage:', e);
    }
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar token al cargar la aplicación (carga rápida desde localStorage)
  useEffect(() => {
    const initAuth = async () => {
      // Verificar si hay datos preservados después de una actualización PWA
      const pwaPending = safeLocalStorage.getItem('pwa_update_pending');
      if (pwaPending === 'true') {
        console.log('🔄 PWA Update: Restaurando datos después de actualización...');
        
        // Restaurar datos preservados
        const preservedToken = safeLocalStorage.getItem('pwa_preserve_token');
        const preservedUser = safeLocalStorage.getItem('pwa_preserve_user');
        const preservedTheme = safeLocalStorage.getItem('pwa_preserve_theme');
        const preservedPrefs = safeLocalStorage.getItem('pwa_preserve_prefs');
        const preservedHealth = safeLocalStorage.getItem('pwa_preserve_health');
        const preservedDashboard = safeLocalStorage.getItem('pwa_preserve_dashboard');
        
        // Restaurar datos de autenticación
        if (preservedToken && preservedUser) {
          safeLocalStorage.setItem('token', preservedToken);
          safeLocalStorage.setItem('user', preservedUser);
          console.log('✅ Datos de autenticación restaurados');
        }
        
        // Restaurar otras configuraciones
        if (preservedTheme) {
          safeLocalStorage.setItem('theme', preservedTheme);
        }
        if (preservedPrefs) {
          safeLocalStorage.setItem('userPreferences', preservedPrefs);
        }
        if (preservedHealth) {
          safeLocalStorage.setItem('healthMode', preservedHealth);
        }
        if (preservedDashboard) {
          safeLocalStorage.setItem('dashboardView', preservedDashboard);
        }
        
        // Limpiar todos los flags de preservación
        safeLocalStorage.removeItem('pwa_update_pending');
        safeLocalStorage.removeItem('pwa_preserve_token');
        safeLocalStorage.removeItem('pwa_preserve_user');
        safeLocalStorage.removeItem('pwa_preserve_theme');
        safeLocalStorage.removeItem('pwa_preserve_prefs');
        safeLocalStorage.removeItem('pwa_preserve_health');
        safeLocalStorage.removeItem('pwa_preserve_dashboard');
        
        console.log('🎉 PWA Update: Restauración completada');
      }

      const token = safeLocalStorage.getItem('token');
      const userData = safeLocalStorage.getItem('user');

      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          // Confiar en el localStorage inicialmente para carga rápida
          dispatch({ type: 'SET_USER', payload: { user, token } });
          
          // DESHABILITADO: No verificar token para evitar rate limiting
          // setTimeout(async () => {
          //   try {
          //     await authAPI.me();
          //   } catch (error) {
          //     safeLocalStorage.removeItem('token');
          //     safeLocalStorage.removeItem('user');
          //     dispatch({ type: 'CLEAR_USER' });
          //   }
          // }, 5000);
          
        } catch (error) {
          // Datos corruptos en localStorage
          safeLocalStorage.removeItem('token');
          safeLocalStorage.removeItem('user');
          dispatch({ type: 'CLEAR_USER' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('🔑 AuthContext: Starting login with credentials:', credentials.email);
      dispatch({ type: 'SET_LOADING', payload: true });
      
      console.log('📡 AuthContext: Calling authAPI.login...');
      const response = await authAPI.login(credentials);
      console.log('📡 AuthContext: API response received:', response);
      
      const { user, token } = response.data.data;
      console.log('👤 AuthContext: User data extracted:', user);
      console.log('🎫 AuthContext: Token extracted:', token ? 'YES' : 'NO');
      
      // Guardar en localStorage
      safeLocalStorage.setItem('token', token);
      safeLocalStorage.setItem('user', JSON.stringify(user));
      console.log('💾 AuthContext: Data saved to localStorage');
      
      dispatch({ type: 'SET_USER', payload: { user, token } });
      console.log('✅ AuthContext: Login completed successfully');
    } catch (error: any) {
      console.error('❌ AuthContext: Login error:', error);
      console.error('❌ AuthContext: Error response:', error.response);
      console.error('❌ AuthContext: Error status:', error.response?.status);
      console.error('❌ AuthContext: Error data:', error.response?.data);
      dispatch({ type: 'SET_LOADING', payload: false });
      
      // Mapear errores técnicos a mensajes user-friendly
      let errorMessage = 'Error al iniciar sesión';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Email o contraseña incorrectos';
      } else if (error.message?.includes('token') || error.message?.includes('Token')) {
        errorMessage = 'Email o contraseña incorrectos';
      }
      
      throw new Error(errorMessage);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await authAPI.register(data);
      const { user, token } = response.data.data;
      
      // Guardar en localStorage
      safeLocalStorage.setItem('token', token);
      safeLocalStorage.setItem('user', JSON.stringify(user));
      
      dispatch({ type: 'SET_USER', payload: { user, token } });
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error(error.response?.data?.message || 'Error al registrarse');
    }
  };

  const loginWithToken = async (token: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Guardar token temporalmente para hacer la petición
      safeLocalStorage.setItem('token', token);
      
      // Obtener información del usuario con el token
      const response = await authAPI.me();
      const user = response.data.data.user;
      
      // Guardar en localStorage
      safeLocalStorage.setItem('user', JSON.stringify(user));
      
      dispatch({ type: 'SET_USER', payload: { user, token } });
    } catch (error: any) {
      // Limpiar si hay error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error(error.response?.data?.message || 'Error al autenticar con token');
    }
  };

  const logout = () => {
    safeLocalStorage.removeItem('token');
    safeLocalStorage.removeItem('user');
    dispatch({ type: 'CLEAR_USER' });
  };

  const refreshToken = async () => {
    try {
      const response = await authAPI.refresh();
      const { token } = response.data.data;
      
      localStorage.setItem('token', token);
      dispatch({ type: 'SET_USER', payload: { user: state.user!, token } });
    } catch (error) {
      logout();
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.me();
      const user = response.data.data.user;
      
      // Actualizar localStorage
      localStorage.setItem('user', JSON.stringify(user));
      
      // Actualizar contexto
      dispatch({ type: 'UPDATE_USER', payload: user });
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const loginWithBiometric = async (userToken: string, userData: any) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Guardar en localStorage
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      dispatch({ type: 'SET_USER', payload: { user: userData, token: userToken } });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error(error.response?.data?.message || 'Error en autenticación biométrica');
    }
  };

  const startImpersonation = async (userId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await impersonationAPI.startImpersonation(userId);
      const { impersonationToken, targetUser } = response.data.data;
      
      // Guardar token de impersonificación
      localStorage.setItem('token', impersonationToken);
      localStorage.setItem('user', JSON.stringify(targetUser));
      
      dispatch({ type: 'SET_USER', payload: { user: targetUser, token: impersonationToken } });
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error(error.response?.data?.message || 'Error al iniciar impersonificación');
    }
  };

  const stopImpersonation = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await impersonationAPI.stopImpersonation();
      const { adminToken, admin } = response.data.data;
      
      // Restaurar token del admin
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', JSON.stringify(admin));
      
      dispatch({ type: 'SET_USER', payload: { user: admin, token: adminToken } });
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error(error.response?.data?.message || 'Error al detener impersonificación');
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    loginWithToken,
    loginWithBiometric,
    logout,
    refreshToken,
    refreshUser,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};